import { Platform } from '@angular/cdk/platform';
import { Component, ElementRef, EventEmitter, HostListener, Input, Output, ViewChild } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatDialog } from '@angular/material/dialog';
import { Capacitor } from '@capacitor/core';
import { BackButtonService } from '../back-button.service';
import { FileService } from '../services/file.service';
import { ChooseFileSourceComponent } from './choose-file-source/choose-file-source.component';
import { FileItem } from './file-selection-dialog/file-item.interface';
import { FileSelectionDialogComponent } from './file-selection-dialog/file-selection-dialog.component';

@Component({
    selector: 'file-manager',
    templateUrl: './file-manager.component.html',
    standalone: false,
})
export class FileManagerComponent {
    isIos = false;
    dragOver = false;
    @Input() fileType: any;
    @Input() secure = '';
    @Input() path: string;
    @Input() hide = false;
    @Input() multiple = false;
    @Input() remove = false;
    @Input() icon: string;
    @Input() currentFile: string;
    @Output() loading = new EventEmitter<any>();
    @Output() selected = new EventEmitter();
    @Output() callback = new EventEmitter<any>();

    @ViewChild('uploadInput') uploadInput: ElementRef;

    directCamera = Capacitor.isNativePlatform();

    sheetDismissSubs = null;

    constructor(
        private matBottomSheet: MatBottomSheet,
        private backButtonService: BackButtonService,
        public platform: Platform,
        private fileService: FileService,
        private matDialog: MatDialog
    ) {
        this.isIos = this.platform.IOS;
    }

    chooseSource() {
        if (!this.sheetDismissSubs && this.directCamera) {
            const ref = this.matBottomSheet.open(ChooseFileSourceComponent, {
                data: {
                    rotate: !!this.currentFile,
                    remove: this.remove && !!this.currentFile,
                    currentFile: this.currentFile,
                },
            });
            this.sheetDismissSubs = ref.afterDismissed().subscribe(type => {
                if (type === 'camera') {
                    this.openModal(null, true);
                }
                if (type === 'gallery') {
                    this.openModal(null, true, true);
                }
                if (type === 'remove') {
                    this.callback.emit(null);
                }
                if (type === 'rotate') {
                    const path = this.path ? `/${this.path}/${this.currentFile}` : this.currentFile;
                    this.fileService.rotateImage(path, !this.path, this.secure).subscribe(file =>
                        setTimeout(() => {
                            this.callback.emit(null);
                            setTimeout(() =>
                                this.callback.emit({
                                    file: {
                                        filename: file.data.basename,
                                        pathname: this.secure ? `${file.data.path}` : `/media${file.data.path}`,
                                        mime: file.data.extension,
                                    },
                                    fileType: this.fileType,
                                })
                            );
                        })
                    );
                }
                this.backButtonService.setBackbuttonAction(null);
                this.sheetDismissSubs.unsubscribe();
                this.sheetDismissSubs = null;
            });
            this.backButtonService.setBackbuttonAction(() => {
                ref.dismiss();
            });
        }
    }

    uploadFile(event) {
        event.stopPropagation();
        event.preventDefault();
        const files = event.srcElement.files;
        this.openModal(files);
        this.loading.emit(files);
    }

    openModal(files?: File[], directCamera?: boolean, photoLib?: boolean) {
        const dialogRef = this.matDialog.open(FileSelectionDialogComponent, {
            panelClass: 'd-none',
            hasBackdrop: false,
            disableClose: true,
            width: '700px',
            height: '500px',
            data: {
                photoLib,
                directCamera,
                secure: this.secure,
                multiple: this.multiple,
                files,
                currentPath: this.path,
            },
        });
        const subs = dialogRef.afterClosed().subscribe((files: FileItem[]) => {
            if (this.uploadInput) {
                this.uploadInput.nativeElement.value = null;
            }
            if (files) {
                const selectedFiles = files.map(f => {
                    return {
                        filename: f.basename,
                        pathname: this.secure ? `${f.path}` : `/media${f.path}`,
                        mime: f.extension,
                    };
                });
                const availableFiles = this.multiple ? selectedFiles : selectedFiles[0];
                this.callback.emit({
                    file: availableFiles,
                    fileType: this.fileType,
                });
            }
            this.selected.emit();
            subs.unsubscribe();
        });
    }

    @HostListener('dragover', ['$event'])
    public ondragover(evt) {
        evt.preventDefault();
        evt.stopPropagation();
        this.dragOver = true;
    }

    @HostListener('dragleave', ['$event'])
    public ondragleave(evt) {
        evt.preventDefault();
        evt.stopPropagation();
        if (evt.srcElement.classList.contains('dialog-container') || evt.srcElement.classList.contains('overlay')) {
            this.dragOver = false;
        }
    }

    @HostListener('drop', ['$event'])
    public ondrop(evt) {
        evt.preventDefault();
        evt.stopPropagation();
        this.dragOver = false;
        const files = evt.dataTransfer.files;
        this.openModal(files);
    }
}
